import React, { useEffect, useState } from "react";
import { ClipboardList, Plus, RefreshCw } from "lucide-react";
import { fetchMyRequests } from "../../api";
import { RequestTimeline } from "./RequestTimeline";
import { AttachmentsBlock } from "./AttachmentsBlock";

const STATUS_STYLES = {
  submitted: "bg-blue-500/10 text-blue-300 border-blue-500/40",
  reviewing: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  in_progress: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
  completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  cancelled: "bg-rose-500/10 text-rose-400 border-rose-500/30",
};

const CustomerDashboard = ({ user, onNewRequest }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = () => fetchMyRequests().then(setData).catch(() => setError(true));
  useEffect(() => { load(); }, []);

  if (error)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-rose-400" data-testid="customer-dashboard">
        Could not load your requests — please sign in again.
      </div>
    );
  if (!data)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-zinc-400 animate-pulse" data-testid="customer-dashboard">
        Loading your requests…
      </div>
    );

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar p-8" data-testid="customer-dashboard">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="border-b border-white/10 pb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white">Welcome{data.user.full_name ? `, ${data.user.full_name}` : ""}</h1>
            <p className="text-zinc-400 text-xs mt-1">Track every service request you've submitted to the firm.</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              data-testid="refresh-requests-btn"
              onClick={load}
              className="p-2 text-zinc-400 hover:text-white border border-white/10 rounded-md transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              data-testid="new-request-btn"
              onClick={onNewRequest}
              className="px-3.5 py-2 bg-white hover:bg-zinc-200 text-black text-xs font-bold rounded-md transition-colors flex items-center space-x-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>New Request</span>
            </button>
          </div>
        </div>

        {data.requests.length === 0 ? (
          <div className="glass-card rounded-md border border-white/10 p-12 text-center space-y-3" data-testid="no-requests-empty-state">
            <ClipboardList className="w-8 h-8 text-zinc-600 mx-auto" />
            <p className="text-sm text-zinc-400">No service requests yet.</p>
            <button onClick={onNewRequest} className="text-xs text-blue-400 hover:text-blue-300 font-semibold">
              Submit your first request →
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="requests-list">
            {data.requests.map((r) => (
              <div key={r.id} className="glass-card rounded-md border border-white/10 p-5 space-y-3" data-testid={`request-row-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="font-mono text-[11px] text-zinc-500">{r.id}</span>
                    <span className="text-sm font-bold text-white capitalize">{r.service_type.replace(/_/g, " ")}</span>
                  </div>
                  <span
                    data-testid={`request-status-${r.id}`}
                    className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border uppercase ${STATUS_STYLES[r.status] || STATUS_STYLES.submitted}`}
                  >
                    {r.status.replace(/_/g, " ")}
                  </span>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">{r.description}</p>
                <RequestTimeline request={r} />
                <AttachmentsBlock request={r} canUpload onChanged={load} />
                <div className="text-[10px] text-zinc-600 font-mono">Submitted {new Date(r.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;
